import { isBrowser, getBrowserApiConfig } from './config';
import { getHeaderValue } from './headers';
import { getServerStampedHeaders } from './server';
import { getSessionTenantId } from './token';

export type ResolveTenantIdArgs = {
  tenantId?: string | null;
  headers?: HeadersInit;

  /**
   * If true, tenant may be derived from SuperTokens session payload.
   * Keep false for calls that should not touch Session at all (e.g. translations, public content).
   */
  allowSession?: boolean;
};

/**
 * Tenant resolution order:
 * 1) explicit args.tenantId
 * 2) caller headers (x-tenant-id)
 * 3) browser session payload tenant (optional: allowSession)
 * 4) browser runtime config tenant
 * 5) server stamped headers / host-derived tenant
 */
export const resolveTenantId = async (
  args: ResolveTenantIdArgs = {},
): Promise<string | null> => {
  if (args.tenantId) return args.tenantId;

  const fromHeaders =
    getHeaderValue(args.headers, 'x-tenant-id') ??
    getHeaderValue(args.headers, 'x-tenantid') ??
    getHeaderValue(args.headers, 'X-TENANT-ID') ??
    getHeaderValue(args.headers, 'X-Tenant-Id');

  if (fromHeaders) return fromHeaders;

  if (isBrowser()) {
    if (args.allowSession) {
      const sessionTid = await getSessionTenantId();
      if (sessionTid) return sessionTid;
    }

    const cfg = getBrowserApiConfig();
    return cfg.tenantId || null;
  }

  const stamped = await getServerStampedHeaders();
  return stamped.tenantId;
};
